import { useState, useEffect, forwardRef } from "react";
import styles from "./AnswerInput.module.css";

interface AnswerInputProps {
  onSubmit: (content: string, isPaid: boolean) => Promise<void>;
  disabled?: boolean;
  prizePool?: number;
  prizeHintThreshold?: number; // Min pool in cents to show "Win up to $X" hint
  maxLength?: number; // Max characters for answer
  // Controlled mode props (optional)
  content?: string;
  onContentChange?: (content: string) => void;
}

const DEFAULT_MAX_LENGTH = 1500;
const PAID_PREFERENCE_KEY = "totemfeed_paid_preference";

export const AnswerInput = forwardRef<HTMLTextAreaElement, AnswerInputProps>(
  function AnswerInput(
    {
      onSubmit,
      disabled = false,
      prizePool = 0,
      prizeHintThreshold = 0,
      maxLength = DEFAULT_MAX_LENGTH,
      content: controlledContent,
      onContentChange,
    },
    ref
  ) {
    // Support both controlled and uncontrolled modes
    const [internalContent, setInternalContent] = useState("");
    const content =
      controlledContent !== undefined ? controlledContent : internalContent;
    const setContent = onContentChange || setInternalContent;
    const [isPaid, setIsPaid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Load paid preference from cookie/localStorage
    useEffect(() => {
      const saved = localStorage.getItem(PAID_PREFERENCE_KEY);
      if (saved === "true") {
        setIsPaid(true);
      }
    }, []);

    // Save paid preference
    const handlePaidChange = (checked: boolean) => {
      setIsPaid(checked);
      localStorage.setItem(PAID_PREFERENCE_KEY, String(checked));
    };

    const handleSubmit = async () => {
      if (!content.trim() || isSubmitting || disabled) return;

      setError(null);
      setIsSubmitting(true);

      try {
        await onSubmit(content.trim(), isPaid);
        // Clear content after successful submit
        if (onContentChange) {
          onContentChange("");
        } else {
          setInternalContent("");
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to submit answer"
        );
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSubmit();
      }
    };

    const remainingChars = maxLength - content.length;
    const potentialWinnings =
      prizePool > 0 ? ((prizePool + 1) * 0.9).toFixed(2) : "0.01";

    return (
      <div className={styles.container}>
        <div className={styles.toggleRow}>
          <label className={styles.toggle}>
            <span
              className={`${styles.toggleLabel} ${
                !isPaid ? styles.toggleLabelActive : ""
              }`}
            >
              Free
            </span>
            <input
              type="checkbox"
              className={styles.toggleInput}
              checked={isPaid}
              onChange={(e) => handlePaidChange(e.target.checked)}
              disabled={disabled || isSubmitting}
            />
            <span className={styles.toggleSlider}></span>
            <span
              className={`${styles.toggleLabel} ${
                isPaid ? styles.toggleLabelActive : ""
              }`}
            >
              Paid ($0.01)
            </span>
          </label>
          {isPaid && prizePool >= prizeHintThreshold && (
            <span className={styles.prizeHint}>
              Win up to ${potentialWinnings}
            </span>
          )}
        </div>

        <textarea
          ref={ref}
          className={styles.textarea}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Share your thoughts anonymously..."
          maxLength={maxLength}
          disabled={disabled || isSubmitting}
          rows={4}
          autoComplete="off"
          inputMode="text"
          data-form-type="other"
        />

        <div className={styles.inputFooter}>
          <span
            className={`${styles.charCount} ${
              remainingChars < 100 ? styles.charCountLow : ""
            }`}
          >
            {remainingChars}
          </span>

          <button
            className={styles.submitBtn}
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting || disabled}
            aria-label="Submit answer"
          >
            {isSubmitting ? (
              <span className={styles.spinner}></span>
            ) : (
              <SendIcon />
            )}
          </button>
        </div>

        {error && <p className={styles.error}>{error}</p>}

        <p className={styles.hint}>
          {isPaid
            ? "Pay $0.01 to enter the prize pool. Anonymous. No refunds."
            : "Post for free. Only paid answers can win the pot."}
        </p>
      </div>
    );
  }
);

function SendIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
